// src/components/PageTransition.js
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import styled, { keyframes, css } from "styled-components";
import logo from "../assets/logo.png";

/* ============================================================
   KEYFRAMES
============================================================ */

const gridDrift = keyframes`
  from { transform: translateY(0); }
  to   { transform: translateY(60px); }
`;

const rippleExpand = keyframes`
  0%   { transform: translate(-50%, -50%) scale(0.5); opacity: 0.5; }
  100% { transform: translate(-50%, -50%) scale(2.8); opacity: 0; }
`;

const logoIn = keyframes`
  0%   { opacity: 0; transform: scale(0.55) rotate(-8deg); }
  65%  { opacity: 1; transform: scale(1.06) rotate(2deg); }
  85%  { transform: scale(0.97) rotate(0deg); }
  100% { opacity: 1; transform: scale(1) rotate(0deg); }
`;

const pulseGlow = keyframes`
  0%   { filter: drop-shadow(0 0 10px rgba(41,171,226,0.65)) drop-shadow(0 0 28px rgba(41,171,226,0.25)); }
  50%  { filter: drop-shadow(0 0 22px rgba(247,148,29,0.90)) drop-shadow(0 0 50px rgba(247,148,29,0.3)); }
  100% { filter: drop-shadow(0 0 10px rgba(41,171,226,0.65)) drop-shadow(0 0 28px rgba(41,171,226,0.25)); }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shinePass = keyframes`
  0%   { transform: translateX(-250%) skewX(-20deg); }
  100% { transform: translateX(500%) skewX(-20deg); }
`;

const textGlow = keyframes`
  0%, 100% { text-shadow: 0 0 14px rgba(41,171,226,0.5), 0 0 40px rgba(41,171,226,0.2); }
  50%       { text-shadow: 0 0 22px rgba(247,148,29,0.7), 0 0 70px rgba(247,148,29,0.25); }
`;

const scanLine = keyframes`
  0%   { transform: translateY(-100%); opacity: 0; }
  8%   { opacity: 1; }
  92%  { opacity: 1; }
  100% { transform: translateY(100vh); opacity: 0; }
`;

/* ============================================================
   STYLED COMPONENTS
============================================================ */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background:
    radial-gradient(ellipse at 25% 15%, rgba(15,74,110,0.9) 0%, transparent 55%),
    radial-gradient(ellipse at 75% 85%, rgba(20,40,70,0.7) 0%, transparent 50%),
    linear-gradient(160deg, #071d2e 0%, #030e1a 60%, #04111f 100%);
  z-index: 9998;
  overflow: hidden;

  /* Fade in / out controlled by $fading prop */
  opacity: ${({ $fading }) => ($fading ? 0 : 1)};
  transform: ${({ $fading }) => ($fading ? "scale(1.025)" : "scale(1)")};
  transition: opacity 0.35s ease, transform 0.35s ease;
`;

const GridBg = styled.div`
  position: absolute;
  inset: -60px 0 0 0;
  background-image:
    linear-gradient(rgba(41,171,226,0.065) 1px, transparent 1px),
    linear-gradient(90deg, rgba(41,171,226,0.065) 1px, transparent 1px);
  background-size: 60px 60px;
  animation: ${gridDrift} 5s linear infinite;
  mask-image: radial-gradient(ellipse 80% 70% at center, black 30%, transparent 85%);
`;

const ScanLineDiv = styled.div`
  position: absolute;
  left: 0;
  width: 100%;
  height: 3px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(41,171,226,0.25) 20%,
    rgba(247,148,29,0.18) 50%,
    rgba(41,171,226,0.25) 80%,
    transparent 100%
  );
  animation: ${scanLine} 2.5s linear infinite;
`;

const RippleRing = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 170px;
  height: 170px;
  border-radius: 50%;
  border: 1.5px solid rgba(41,171,226,0.28);
  animation: ${rippleExpand} 2s ease-out infinite;
  animation-delay: ${({ $delay }) => $delay ?? "0s"};
`;

const Center = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const LogoContainer = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  margin-bottom: 1.4rem;
`;

const LogoImg = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 20px;
  position: relative;
  z-index: 2;
  opacity: 0;
  animation:
    ${logoIn} 0.65s cubic-bezier(0.175, 0.885, 0.32, 1.275) 0.05s forwards,
    ${pulseGlow} 2.5s 0.8s ease-in-out infinite;
`;

const ShineOverlay = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 20px;
  overflow: hidden;
  z-index: 3;
  pointer-events: none;

  &::before {
    content: "";
    position: absolute;
    top: 0; left: 0;
    width: 44px;
    height: 100%;
    background: rgba(255, 255, 255, 0.38);
    filter: blur(9px);
    animation: ${shinePass} 2.5s ease-in-out infinite;
    animation-delay: 0.8s;
  }
`;

const BrandName = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.6rem, 3.5vw, 2.1rem);
  font-weight: 800;
  letter-spacing: 0.09em;
  margin: 0 0 0.35rem;
  background: linear-gradient(135deg, #29ABE2 0%, #e0f4ff 45%, #F7941D 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  opacity: 0;
  animation:
    ${fadeInUp} 0.5s ease 0.55s forwards,
    ${textGlow} 2.5s 1.1s ease-in-out infinite;
`;

const Tagline = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(0.62rem, 1.3vw, 0.74rem);
  font-weight: 400;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(160, 215, 240, 0.65);
  margin: 0;
  opacity: 0;
  animation: ${fadeInUp} 0.5s ease 0.75s forwards;
`;

/* ============================================================
   COMPONENT
============================================================ */

const PageTransition = () => {
  const location = useLocation();
  const prevPath = useRef(null);
  const [visible, setVisible] = useState(false);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    // Skip the very first render — initial load is handled by SplashScreen
    if (prevPath.current === null) {
      prevPath.current = location.pathname;
      return;
    }

    // No-op if path didn't change
    if (prevPath.current === location.pathname) return;
    prevPath.current = location.pathname;

    // Show overlay
    setFading(false);
    setVisible(true);

    // Start fade-out after 850ms
    const fadeTimer = setTimeout(() => setFading(true), 850);
    // Unmount after fade completes
    const hideTimer = setTimeout(() => {
      setVisible(false);
      setFading(false);
    }, 1220);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  }, [location.pathname]);

  if (!visible) return null;

  return (
    <Overlay $fading={fading}>
      <GridBg />
      <ScanLineDiv />
      <RippleRing $delay="0s" />
      <RippleRing $delay="0.65s" />
      <RippleRing $delay="1.3s" />

      <Center>
        <LogoContainer>
          <LogoImg src={logo} alt="Trend Genie" />
          <ShineOverlay />
        </LogoContainer>
        <BrandName>TREND GENIE</BrandName>
        <Tagline>AI-Powered Investment Analysis</Tagline>
      </Center>
    </Overlay>
  );
};

export default PageTransition;
